import {useCallback, useMemo} from 'react';
import {AddToCartButtonPlacement, IProductOption, ProductOptionsDisplayOption} from '../../../../../types/galleryTypes';
import {useGlobals} from '../../../../../globalPropsContext';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Experiments} from '../../../../../constants';

export const useQuickAddOptions = (options: IProductOption[]) => {
  const {styles, stylesParams, shouldShowMobile} = useGlobals();
  const {experiments} = useExperiments();

  const numberOfColorOptions = useMemo(() => {
    return options.filter((option) => option.optionType === ProductOptionType.COLOR).length;
  }, [options]);

  const numberOfDropdownOptions = useMemo(() => {
    return options.filter((option) => option.optionType === ProductOptionType.DROP_DOWN).length;
  }, [options]);

  const removeFirstOptionOfType = useCallback((currentOptions: IProductOption[], optionType: ProductOptionType) => {
    const index = currentOptions.findIndex((option) => option.optionType === optionType);
    currentOptions.splice(index, 1);
  }, []);

  const getProductQuickAddFilteredOptions = useCallback(() => {
    const visibleOptions = Array.from(options);
    const isShowFirstProductOptionInGalleryExperimentEnabled = experiments.enabled(
      Experiments.ShouldShowFirstProductOptionInGallery
    );
    const galleryOptionsDisplayType = styles.get(stylesParams.gallery_productOptionsDisplayOption);
    const isShowColorOptionsOnlyDisabled = !styles.get(stylesParams.gallery_showColorOptionsOnly);

    if (
      isShowFirstProductOptionInGalleryExperimentEnabled &&
      galleryOptionsDisplayType === ProductOptionsDisplayOption.FIRST_ANY_OPTION
    ) {
      const firstOptionType = visibleOptions[0].optionType as ProductOptionType;
      removeFirstOptionOfType(visibleOptions, firstOptionType);
      return visibleOptions;
    }

    if (numberOfColorOptions > 0) {
      removeFirstOptionOfType(visibleOptions, ProductOptionType.COLOR);
    }

    const shouldRemoveAnyDropdownOption =
      galleryOptionsDisplayType === ProductOptionsDisplayOption.FIRST_TWO_OPTIONS ||
      (!isShowFirstProductOptionInGalleryExperimentEnabled && isShowColorOptionsOnlyDisabled);
    if (shouldRemoveAnyDropdownOption) {
      if (numberOfDropdownOptions > 0) {
        removeFirstOptionOfType(visibleOptions, ProductOptionType.DROP_DOWN);
      }

      const shouldRemoveTheSecondDropdown = numberOfColorOptions === 0 && numberOfDropdownOptions > 1;
      if (shouldRemoveTheSecondDropdown) {
        removeFirstOptionOfType(visibleOptions, ProductOptionType.DROP_DOWN);
      }
    }

    return visibleOptions;
  }, [
    removeFirstOptionOfType,
    numberOfColorOptions,
    numberOfDropdownOptions,
    options,
    styles,
    stylesParams,
    experiments,
  ]);

  return useMemo(() => {
    const hasOptions = options.length > 0;
    const isDesktop = !shouldShowMobile;
    const isShowProductOptionsButtonEnabled = styles.get(stylesParams.gallery_showProductOptionsButton);
    const isAddToCartButtonOnImage =
      styles.get(stylesParams.gallery_addToCartButtonPlacement) === AddToCartButtonPlacement.OnImage;
    const shouldFilterQuickAddOptions =
      hasOptions && isDesktop && isShowProductOptionsButtonEnabled && isAddToCartButtonOnImage;

    return shouldFilterQuickAddOptions ? getProductQuickAddFilteredOptions() : options;
  }, [options, styles, stylesParams, shouldShowMobile, getProductQuickAddFilteredOptions]);
};
